// Preset buttons + From/To date inputs (architecture sprint-05 §6.11), used by the tracker and the team
// report. Ranges are validated inline (≤ 366 days, ordered); nothing is requested until valid.
import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';
import { isValidReportRange, type DateRange } from '@/lib/report-model';

export const RANGE_ORDER_MESSAGE = 'To must be on or after From';
export const RANGE_LENGTH_MESSAGE = 'Range can be at most 366 days';

export interface RangePreset {
  key: string;
  label: string;
  range: DateRange;
}

interface RangePickerProps {
  from: string;
  to: string;
  presets: RangePreset[];
  onChange: (range: DateRange) => void;
}

/** null when usable; otherwise the inline message. */
export function rangeError(from: string, to: string): string | null {
  if (!from || !to) return null;
  if (to < from) return RANGE_ORDER_MESSAGE;
  return isValidReportRange(from, to) ? null : RANGE_LENGTH_MESSAGE;
}

export function RangePicker({ from, to, presets, onChange }: RangePickerProps): JSX.Element {
  const id = useId();
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraftFrom(from);
    setDraftTo(to);
    setError(null);
  }, [from, to]);

  function apply(nextFrom: string, nextTo: string): void {
    setDraftFrom(nextFrom);
    setDraftTo(nextTo);
    if (!nextFrom || !nextTo) return;
    const message = rangeError(nextFrom, nextTo);
    setError(message);
    if (message === null) onChange({ from: nextFrom, to: nextTo });
  }

  return (
    <div className="card mb-6">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-from`}>From</Label>
          <Input id={`${id}-from`} type="date" value={draftFrom} onChange={(e) => apply(e.target.value, draftTo)} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-to`}>To</Label>
          <Input id={`${id}-to`} type="date" value={draftTo} onChange={(e) => apply(draftFrom, e.target.value)} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2" aria-label="Range presets">
        {presets.map((p) => {
          const pressed = p.range.from === from && p.range.to === to;
          return (
            <Button
              key={p.key}
              variant="ghost"
              size="xs"
              aria-pressed={pressed}
              className={cn(pressed && 'bg-accent text-foreground')}
              onClick={() => onChange(p.range)}
            >
              {p.label}
            </Button>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger-fg">
          {error}
        </p>
      )}
    </div>
  );
}
